import React from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes } from 'react-icons/fa';

const LABELS = ['A', 'B', 'C', 'D'];

// Couleurs distinctes pour chaque lettre (état neutre)
const LABEL_COLORS = [
  { bg: '#3b82f6', shadow: 'rgba(59,130,246,0.4)' },   // A — bleu
  { bg: '#8b5cf6', shadow: 'rgba(139,92,246,0.4)' },   // B — violet
  { bg: '#f97316', shadow: 'rgba(249,115,22,0.4)' },   // C — orange
  { bg: '#14b8a6', shadow: 'rgba(20,184,166,0.4)' },   // D — teal
];

function AnswerOption({
  index = 0,
  text,
  selected = false,
  correct = false,
  incorrect = false,
  revealed = false,
  disabled = false,
  onClick,
}) {
  const label = LABELS[index] || 'A';
  const labelColor = LABEL_COLORS[index] || LABEL_COLORS[0];

  // État de la carte réponse
  const getCardStyle = () => {
    if (correct && revealed) {
      return {
        background: 'rgba(34,197,94,0.22)',
        borderColor: '#22c55e',
        boxShadow: '0 0 24px rgba(34,197,94,0.35), inset 0 0 12px rgba(34,197,94,0.1)',
        color: '#ffffff',
      };
    }
    if (incorrect) {
      return {
        background: 'rgba(239,68,68,0.22)',
        borderColor: '#ef4444',
        boxShadow: '0 0 24px rgba(239,68,68,0.35), inset 0 0 12px rgba(239,68,68,0.1)',
        color: '#ffffff',
      };
    }
    if (selected && !revealed) {
      return {
        background: 'rgba(240,180,41,0.15)',
        borderColor: '#f0b429',
        boxShadow: '0 0 16px rgba(240,180,41,0.25)',
        color: '#ffffff',
      };
    }
    if (revealed) {
      return {
        background: 'rgba(255,255,255,0.03)',
        borderColor: 'rgba(255,255,255,0.07)',
        color: 'rgba(255,255,255,0.3)',
      };
    }
    return {
      background: 'rgba(255,255,255,0.07)',
      borderColor: 'rgba(255,255,255,0.15)',
      color: '#ffffff',
    };
  };

  // Couleur du cercle lettre
  const getLabelStyle = () => {
    if (correct && revealed) return { background: '#22c55e', color: '#fff' };
    if (incorrect)            return { background: '#ef4444', color: '#fff' };
    if (selected && !revealed) return { background: '#f0b429', color: '#0d2b1a' };
    if (revealed)              return { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' };
    return { background: labelColor.bg, color: '#fff', boxShadow: `0 2px 8px ${labelColor.shadow}` };
  };

  const cardStyle = getCardStyle();

  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={onClick}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.22, delay: index * 0.06 }}
      whileTap={!disabled ? { scale: 0.97 } : {}}
      // Shake si mauvaise réponse
      {...(incorrect ? {
        animate: {
          opacity: 1, x: [0, -8, 8, -5, 5, 0],
          transition: { duration: 0.4, ease: 'easeInOut' }
        }
      } : {})}
      className="w-full text-left py-3.5 px-4 rounded-xl font-semibold text-base border-2 flex items-center gap-3 transition-colors duration-200"
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: '1.05rem',
        letterSpacing: '0.02em',
        cursor: disabled ? 'default' : 'pointer',
        borderWidth: 2,
        borderStyle: 'solid',
        transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
        ...cardStyle,
      }}
    >
      {/* Cercle lettre */}
      <span
        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-all duration-200"
        style={{ ...getLabelStyle(), minWidth: 32, minHeight: 32 }}
      >
        {/* Icône si révélé, sinon la lettre */}
        {revealed && correct ? (
          <FaCheck size={14} />
        ) : revealed && incorrect ? (
          <FaTimes size={14} />
        ) : (
          label
        )}
      </span>

      {/* Texte */}
      <span className="flex-1 leading-snug">{text}</span>

      {/* Indicateur droit */}
      {revealed && correct && (
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: '#22c55e', boxShadow: '0 0 12px rgba(34,197,94,0.6)' }}
        >
          <FaCheck size={13} color="#fff" />
        </motion.div>
      )}
      {revealed && incorrect && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: '#ef4444', boxShadow: '0 0 12px rgba(239,68,68,0.6)' }}
        >
          <FaTimes size={13} color="#fff" />
        </motion.div>
      )}
    </motion.button>
  );
}

export default AnswerOption;
